import React, { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import "./WarehouseAccessoryPanel.css";

// WAREHOUSE_POS_SUPPLY_REQUEST_INBOX_CLEAN_V3

function formatQty(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function isWarehouseUser(user) {
  return ["WAREHOUSE_STAFF", "WAREHOUSE_MANAGER"].includes(
    String(user?.role || "").toUpperCase()
  );
}

function StatusBadge({ status }) {
  return (
    <strong className={`accessoryStatus-${status || "UNKNOWN"}`}>
      {status || "-"}
    </strong>
  );
}

export default function WarehouseAccessoryPanel({ user, selectedLocation }) {
  const [inventory, setInventory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState("ALL");

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  async function loadInventory() {
    if (!user?.username || !selectedLocation) return;

    try {
      setError("");

      const params = new URLSearchParams({
        username: user.username,
        location: selectedLocation,
      });

      const response = await fetch(
        `${API_BASE}/api/erp/warehouse/accessory-inventory?${params.toString()}`
      );
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load accessory inventory");
      }

      setInventory(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message);
      setInventory([]);
    }
  }

  async function loadRequests() {
    if (!user?.username || !selectedLocation) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        username: user.username,
        location: selectedLocation,
        status,
        limit: "100",
      });

      const response = await fetch(
        `${API_BASE}/api/erp/warehouse/supply-requests?${params.toString()}`
      );
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load POS supply requests");
      }

      setRequests(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }

  async function refreshAll() {
    await loadInventory();
    await loadRequests();
  }

  
  // WAREHOUSE_SUPPLY_SEND_ACTUAL_QTY_PROMPT_V1
  async function sendSupplyRequest(row) {
    try {
      setActionLoading(row.id);
      setError("");
      setSuccessMsg("");

      const qtyOverrides = {}; // WAREHOUSE_SUPPLY_SEND_ACTUAL_QTY_ALWAYS_PROMPT_V2

      for (const item of row.items || []) {
        const requestedQty = Number(item.qty || 0);

        const actual = window.prompt(
          `${item.itemCode} / ${item.itemName || ""}\nRequested Qty: ${formatQty(requestedQty)}\nWarehouse Send Qty ??????`,
          String(requestedQty || "")
        );

        if (actual === null) {
          setActionLoading("");
          return;
        }

        const trimmed = String(actual || "").trim();
        const actualQty = trimmed === "" ? requestedQty : Number(trimmed);

        if (!Number.isFinite(actualQty) || actualQty <= 0) {
          throw new Error(`${item.itemCode} send qty required`);
        }

        if (requestedQty > 0 && actualQty > requestedQty) {
          throw new Error(
            `${item.itemCode} send qty cannot exceed requested qty. Requested ${requestedQty}, send ${actualQty}`
          );
        }

        qtyOverrides[item.id || item.itemCode] = actualQty;
        qtyOverrides[item.itemCode] = actualQty;
      }

      const response = await fetch(
        `${API_BASE}/api/erp/warehouse/supply-requests/${row.id}/send`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username: user.username,
            dispatchNote: "Warehouse sent supplies to shop",
            qtyOverrides,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Send supply request failed");
      }

      setSuccessMsg(data.message || "Supply request sent to shop");
      await refreshAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  async function cancelSupplyRequest(row) {
    const reason = window.prompt("Cancel reason ထည့်ပါ");
    if (!reason) return;

    try {
      setActionLoading(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(
        `${API_BASE}/api/erp/warehouse/supply-requests/${row.id}/cancel`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username: user.username,
            cancelReason: reason,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Cancel supply request failed");
      }

      setSuccessMsg(data.message || "Supply request canceled");
      await loadRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, selectedLocation, status]);

  return (
    <div className="warehouseAccessoryPanel">
      <div className="warehouseAccessoryPanel__head">
        <div>
          <div className="warehouseAccessoryPanel__tag">🛍️ SUPPLIES</div>
          <h2>Carrier Bag / Supplies / Fixed Asset Request Inbox</h2>
          <p>
            POS shop မှ request တင်ထားသော Carrier Bag / Receipt Roll / Fixed Asset
            များကို Warehouse မှ Send သို့မဟုတ် Cancel လုပ်ရန်။
          </p>
        </div>

        <button type="button" onClick={refreshAll} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="warehouseAccessoryAlert warehouseAccessoryAlert--error">
          {error}
        </div>
      )}

      {successMsg && (
        <div className="warehouseAccessoryAlert warehouseAccessoryAlert--success">
          {successMsg}
        </div>
      )}

      <div className="warehouseAccessoryInventory">
        <h3>{selectedLocation} Supplies / Accessory Stock</h3>

        <table className="warehouseAccessoryTable">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Category</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Low</th>
            </tr>
          </thead>

          <tbody>
            {inventory.length === 0 ? (
              <tr>
                <td colSpan="6" className="warehouseAccessoryEmpty">
                  No accessory stock rows.
                </td>
              </tr>
            ) : (
              inventory.map((row) => (
                <tr key={row.itemCode}>
                  <td>{row.itemCode}</td>
                  <td>{row.itemName}</td>
                  <td>{row.category}</td>
                  <td
                    className={
                      Number(row.qty || 0) <= Number(row.minStock || 0)
                        ? "accessoryLowQty"
                        : ""
                    }
                  >
                    {formatQty(row.qty)}
                  </td>
                  <td>{row.unit}</td>
                  <td>{row.isLow ? "⚠️ Low" : "OK"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="warehouseAccessoryIssueHead">
        <h3>POS Supplies / Fixed Asset Requests</h3>

        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ALL">All</option>
          <option value="POS_REQUESTED">POS Requested</option>
          <option value="WAREHOUSE_SENT">Warehouse Sent</option>
          <option value="CONFIRMED_RECEIVED">Confirmed Received</option>
          <option value="CANCELED">Canceled</option>
        </select>
      </div>

      <div className="warehouseAccessoryIssueList">
        {requests.length === 0 ? (
          <div className="warehouseAccessoryEmpty">No POS supplies request.</div>
        ) : (
          requests.map((row) => (
            <div key={row.id} className="warehouseAccessoryCard">
              <div className="warehouseAccessoryCard__top">
                <div>
                  <b>{row.issueNo}</b>
                  <span>
                    {row.warehouseLocationCode} → {row.shopLocationCode}
                  </span>
                </div>

                <StatusBadge status={row.status} />
              </div>

              <div className="warehouseAccessoryMeta">
                <span>Items: {row.itemRows}</span>
                <span>Total Qty: {formatQty(row.totalQty)}</span>
                <span>Requested: {formatDate(row.requestedAt)}</span>
                {row.dispatchedAt && (
                  <span>Sent: {formatDate(row.dispatchedAt)}</span>
                )}
                {row.receivedAt && (
                  <span>Received: {formatDate(row.receivedAt)}</span>
                )}
              </div>

              <div className="warehouseAccessoryItems">
                {(row.items || []).map((item) => (
                  <span key={item.id || item.itemCode}>
                    {item.itemCode} × {formatQty(item.qty)} {item.unit}
                  </span>
                ))}
              </div>

              {row.requestNote && (
                <div className="warehouseAccessoryNote">
                  Request Note: {row.requestNote}
                </div>
              )}

              {row.cancelReason && (
                <div className="warehouseAccessoryCancelReason">
                  Cancel Reason: {row.cancelReason}
                </div>
              )}

              {isWarehouseUser(user) && row.status === "POS_REQUESTED" && (
                <div className="warehouseAccessoryActions">
                  <button
                    type="button"
                    onClick={() => sendSupplyRequest(row)}
                    disabled={actionLoading === row.id}
                  >
                    {actionLoading === row.id ? "Sending..." : "🚚 Send to Shop"}
                  </button>

                  <button
                    type="button"
                    onClick={() => cancelSupplyRequest(row)}
                    disabled={actionLoading === row.id}
                    className="warehouseAccessoryCancelBtn"
                  >
                    ❌ Cancel
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
